/**
 * Position Comment API client methods.
 *
 * Position comments are quick notes separate from structured thesis data.
 */

import { API_CONFIG } from '../../config/api';
import { getToken } from '../tokenStorage';

export interface PositionComment {
  id: number;
  position_id: number;
  user_id: number;
  comment_text: string;
  next_revisit_date: string | null;
  created_at: string;
  updated_at: string;
}

export interface PositionCommentCreateRequest {
  position_id: number;
  comment_text: string;
  next_revisit_date?: string | null;
}

export interface PositionCommentUpdateRequest {
  comment_text?: string;
  next_revisit_date?: string | null;
}

/**
 * Get comments for a specific position.
 */
export async function getPositionComments(positionId: number): Promise<PositionComment[]> {
  const token = await getToken();

  if (!token) {
    throw new Error('Authentication required');
  }

  const url = new URL(`${API_CONFIG.baseURL}/api/v1/position-comments`);
  url.searchParams.append('position_id', positionId.toString());

  const response = await fetch(url.toString(), {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error('Failed to get position comments');
  }

  return response.json();
}

/**
 * Add a new comment to a position.
 */
export async function addPositionComment(
  positionId: number,
  commentText: string,
  nextRevisitDate?: string | null
): Promise<PositionComment> {
  const token = await getToken();

  if (!token) {
    throw new Error('Authentication required');
  }

  const request: PositionCommentCreateRequest = {
    position_id: positionId,
    comment_text: commentText,
    next_revisit_date: nextRevisitDate || null,
  };

  const response = await fetch(`${API_CONFIG.baseURL}/api/v1/position-comments`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to add position comment');
  }

  return response.json();
}

/**
 * Update an existing comment (in-place edit).
 */
export async function updatePositionComment(
  commentId: number,
  commentText?: string,
  nextRevisitDate?: string | null
): Promise<PositionComment> {
  const token = await getToken();

  if (!token) {
    throw new Error('Authentication required');
  }

  const request: PositionCommentUpdateRequest = {};
  if (commentText !== undefined) {
    request.comment_text = commentText;
  }
  if (nextRevisitDate !== undefined) {
    request.next_revisit_date = nextRevisitDate;
  }

  const response = await fetch(`${API_CONFIG.baseURL}/api/v1/position-comments/${commentId}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to update position comment');
  }

  return response.json();
}

/**
 * Delete a comment.
 */
export async function deletePositionComment(commentId: number): Promise<void> {
  const token = await getToken();

  if (!token) {
    throw new Error('Authentication required');
  }

  const response = await fetch(`${API_CONFIG.baseURL}/api/v1/position-comments/${commentId}`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to delete position comment');
  }
}
